import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { Card, Modal, Tooltip } from '@components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface Props {
  todaySalesByHour: any;
  weekSalesByDay: any;
  monthSalesByWeek: any;
  yearSalesByMonth: any;
}

export const RevenueOverview: React.FC<Props> = ({
  todaySalesByHour,
  weekSalesByDay,
  monthSalesByWeek,
  yearSalesByMonth,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | 'year'>('year');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // Gestion sécurisée des données
  const safeData = useMemo(() => {
    return {
      day: todaySalesByHour?.data || [],
      week: weekSalesByDay?.data || [],
      month: monthSalesByWeek?.data || [],
      year: yearSalesByMonth?.data || [],
    };
  }, [todaySalesByHour, weekSalesByDay, monthSalesByWeek, yearSalesByMonth]);

  const selectedData = useMemo(() => {
    switch (selectedPeriod) {
      case 'day':
        return safeData.day.map((item: any) => ({
          label: `${item.hour}`,
          total: item.total,
          salesCount: item.salesCount || 0,
        }));
      case 'week':
        return safeData.week.map((item: any) => ({
          label: item.day,
          total: item.total,
          salesCount: 0, // Pas dans les données actuelles
        }));
      case 'month':
        return safeData.month.map((item: any) => ({
          label: item.week,
          total: item.total,
          salesCount: 0, // Pas dans les données actuelles
        }));
      case 'year':
        return safeData.year.map((item: any) => ({
          label: item.month,
          total: item.total,
          salesCount: 0, // Pas dans les données actuelles
        }));
      default:
        return [];
    }
  }, [selectedPeriod, safeData]);

  const chartData = useMemo(() => {
    return {
      labels: selectedData.map((item: any) => item.label),
      datasets: [
        {
          label: 'Revenus (FCFA)',
          data: selectedData.map((item: any) => item.total),
          backgroundColor:
            selectedPeriod === 'day'
              ? 'rgba(59, 130, 246, 0.5)'
              : selectedPeriod === 'week'
              ? 'rgba(16, 185, 129, 0.5)'
              : selectedPeriod === 'month'
              ? 'rgba(139, 92, 246, 0.5)'
              : 'rgba(245, 158, 11, 0.5)',
          borderColor:
            selectedPeriod === 'day'
              ? 'rgb(59, 130, 246)'
              : selectedPeriod === 'week'
              ? 'rgb(16, 185, 129)'
              : selectedPeriod === 'month'
              ? 'rgb(139, 92, 246)'
              : 'rgb(245, 158, 11)',
          borderWidth: 2,
          tension: 0.4,
        },
      ],
    };
  }, [selectedData, selectedPeriod]);

  // Calculs des totaux et métriques
  const totals = {
    day: safeData.day.reduce((sum: number, item: any) => sum + (item.total || 0), 0),
    week: safeData.week.reduce((sum: number, item: any) => sum + (item.total || 0), 0),
    month: safeData.month.reduce((sum: number, item: any) => sum + (item.total || 0), 0),
    year: safeData.year.reduce((sum: number, item: any) => sum + (item.total || 0), 0),
  };

  const salesCount = {
    day: safeData.day.reduce((sum: number, item: any) => sum + (item.salesCount || 0), 0),
    week: weekSalesByDay?.summary?.totalSales || 0,
    month: 0, // Pas disponible dans la structure actuelle
    year: yearSalesByMonth?.summary?.totalSales || 0,
  };

  // Données pour les insights
  const getCurrentSummary = () => {
    switch (selectedPeriod) {
      case 'day':
        return todaySalesByHour?.summary;
      case 'week':
        return weekSalesByDay?.summary;
      case 'month':
        return null; // Pas de summary dans monthSalesByWeek
      case 'year':
        return yearSalesByMonth?.summary;
      default:
        return null;
    }
  };

  const getCurrentAIAnalysis = () => {
    switch (selectedPeriod) {
      case 'day':
        return todaySalesByHour?.aiAnalysis;
      case 'week':
        return weekSalesByDay?.aiAnalysis;
      case 'month':
        return monthSalesByWeek?.aiAnalysis;
      case 'year':
        return yearSalesByMonth?.aiAnalysis;
      default:
        return null;
    }
  };

  const summary = getCurrentSummary();
  const aiAnalysis = getCurrentAIAnalysis();

  const buttonsConfig = [
    {
      period: 'day',
      label: "Aujourd'hui",
      colorClass: 'bg-blue-500 hover:bg-blue-600 text-white',
      value: totals.day,
      sales: salesCount.day,
      icon: '📊',
    },
    {
      period: 'week',
      label: 'Cette semaine',
      colorClass: 'bg-green-500 hover:bg-green-600 text-white',
      value: totals.week,
      sales: salesCount.week,
      icon: '📈',
    },
    {
      period: 'month',
      label: 'Ce mois',
      colorClass: 'bg-purple-500 hover:bg-purple-600 text-white',
      value: totals.month,
      sales: salesCount.month,
      icon: '📅',
    },
    {
      period: 'year',
      label: "Cette année",
      colorClass: 'bg-orange-500 hover:bg-orange-600 text-white',
      value: totals.year,
      sales: salesCount.year,
      icon: '🗓️',
    },
  ];

  // Fonction pour formater les grands nombres
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="space-y-6">
      {/* Cartes de résumé par période */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {buttonsConfig.map(({ period, label, colorClass, value, sales, icon }) => (
          <div
            key={period}
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              selectedPeriod === period
                ? 'ring-2 ring-blue-500 shadow-lg'
                : 'hover:shadow-md'
            }`}
            onClick={() => setSelectedPeriod(period as 'day' | 'week' | 'month' | 'year')}
          >
            <Card customClassName="h-full">
              <div className={`p-4 rounded-lg h-full ${selectedPeriod === period ? colorClass : 'bg-gray-50 dark:bg-gray-800'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-sm font-medium ${selectedPeriod === period ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                      {label}
                    </h3>
                    <p className={`text-xl font-bold ${selectedPeriod === period ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                      {formatNumber(value)} FCFA
                    </p>
                    {sales > 0 && (
                      <p className={`text-xs ${selectedPeriod === period ? 'text-white/80' : 'text-gray-500'}`}>
                        {sales} vente{sales > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <span className="text-2xl">{icon}</span>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>

      {/* Métriques clés de la période sélectionnée */}
      {summary && (
        <Card customClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {summary.bestMonth && (
              <Tooltip content="Le mois avec le plus haut chiffre d'affaires de la période analysée">
                <div className="text-center p-4 bg-gradient-to-br from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-rose-200 dark:border-rose-700">
                  <div className="text-2xl mb-2">🏆</div>
                  <h4 className="font-semibold text-rose-800 dark:text-slate-100 mb-2">Meilleur mois</h4>
                  <p className="text-xl font-bold text-rose-900 dark:text-gray-50 mb-1">
                    {summary.bestMonth.month}
                  </p>
                  <p className="text-sm font-medium text-rose-700 dark:text-stone-200">
                    {formatNumber(summary.bestMonth.total)} FCFA
                  </p>
                </div>
              </Tooltip>
            )}
            {summary.bestDay && (
              <Tooltip content="Le jour de la semaine générant le plus de revenus">
                <div className="text-center p-4 bg-gradient-to-br from-sky-50 to-blue-100 dark:from-sky-900/30 dark:to-blue-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-sky-200 dark:border-sky-700">
                  <div className="text-2xl mb-2">📅</div>
                  <h4 className="font-semibold text-sky-800 dark:text-zinc-100 mb-2">Meilleur jour</h4>
                  <p className="text-xl font-bold text-sky-900 dark:text-neutral-50 mb-1">
                    {summary.bestDay.day}
                  </p>
                  <p className="text-sm font-medium text-sky-700 dark:text-gray-200">
                    {formatNumber(summary.bestDay.total)} FCFA
                  </p>
                </div>
              </Tooltip>
            )}
            {summary.peakHour && (
              <Tooltip content="L'heure de la journée avec le plus de ventes enregistrées">
                <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-indigo-900/30 dark:to-purple-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-indigo-200 dark:border-indigo-700">
                  <div className="text-2xl mb-2">⏰</div>
                  <h4 className="font-semibold text-indigo-800 dark:text-white mb-2">Heure de pic</h4>
                  <p className="text-xl font-bold text-indigo-900 dark:text-slate-50 mb-1">
                    {summary.peakHour.hour}
                  </p>
                  <p className="text-sm font-medium text-indigo-700 dark:text-neutral-200">
                    {formatNumber(summary.peakHour.total)} FCFA
                  </p>
                </div>
              </Tooltip>
            )}
            {summary.averageMonthlySales && (
              <Tooltip content="Montant moyen des ventes par mois calculé sur la période">
                <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/30 dark:to-teal-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-emerald-200 dark:border-emerald-700">
                  <div className="text-2xl mb-2">📊</div>
                  <h4 className="font-semibold text-emerald-800 dark:text-stone-100 mb-2">Moyenne mensuelle</h4>
                  <p className="text-xl font-bold text-emerald-900 dark:text-gray-100">
                    {formatNumber(summary.averageMonthlySales)} FCFA
                  </p>
                </div>
              </Tooltip>
            )}
            {summary.averageDailySales && (
              <Tooltip content="Montant moyen des ventes par jour calculé sur la période">
                <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/30 dark:to-orange-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-amber-200 dark:border-amber-700">
                  <div className="text-2xl mb-2">📈</div>
                  <h4 className="font-semibold text-amber-800 dark:text-zinc-50 mb-2">Moyenne quotidienne</h4>
                  <p className="text-xl font-bold text-amber-900 dark:text-neutral-100">
                    {formatNumber(summary.averageDailySales)} FCFA
                  </p>
                </div>
              </Tooltip>
            )}
          </div>
        </Card>
      )}

      {/* Graphique principal */}
      <Card customClassName="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Évolution des revenus - {buttonsConfig.find(b => b.period === selectedPeriod)?.label}
          </h3>
          {aiAnalysis && (
            <button
              onClick={() => setShowAnalysisModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span className="text-lg">🤖</span>
              Analyse IA
            </button>
          )}
        </div>
        
        <div className="relative h-64 md:h-80">
          {selectedPeriod === 'day' || selectedPeriod === 'week' ? (
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: 'white',
                    bodyColor: 'white',
                    callbacks: {
                      label: function(context) {
                        return `Revenus: ${formatNumber(context.parsed.y)} FCFA`;
                      }
                    }
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { 
                      font: { size: 11 },
                      maxRotation: 45,
                    },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: { 
                      font: { size: 11 },
                      callback: function(value) {
                        return formatNumber(Number(value)) + ' FCFA';
                      }
                    },
                  },
                },
                elements: {
                  point: {
                    radius: 4,
                    hoverRadius: 6,
                  },
                },
              }}
            />
          ) : (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: 'white',
                    bodyColor: 'white',
                    callbacks: {
                      label: function(context) {
                        return `Revenus: ${formatNumber(context.parsed.y)} FCFA`;
                      }
                    }
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { 
                      font: { size: 11 },
                      maxRotation: 45,
                    },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: { 
                      font: { size: 11 },
                      callback: function(value) {
                        return formatNumber(Number(value)) + ' FCFA';
                      }
                    },
                  },
                },
              }}
            />
          )}
        </div>
      </Card>

      {/* Modal d'analyse IA */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title="🤖 Analyse IA"
        customClassName="max-w-4xl"
      >
        {aiAnalysis && (
          <div className="space-y-6">
            {/* Badge de confiance */}
            <div className="flex justify-center">
              <span className="px-4 py-2 text-sm bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full border border-purple-200">
                <span className="font-semibold">Niveau de confiance:</span> {aiAnalysis.confidence ? aiAnalysis.confidence.toFixed(2) : '0.00'}%
              </span>
            </div>

            {/* Métriques IA avec tooltips */}
            {aiAnalysis.metrics && (
              <div>
                <h4 className="text-lg font-bold text-blue-400 mb-4">
                  📊 Métriques de Performance
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {aiAnalysis.metrics.seasonalityIndex !== undefined && (
                    <Tooltip 
                      content="L'index de saisonnalité mesure les variations des ventes selon les périodes. 100 = normal, >100 = forte saisonnalité"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-cyan-50 to-sky-100 dark:from-cyan-900/30 dark:to-sky-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-cyan-300 dark:border-cyan-600">
                        <div className="text-2xl mb-2">🌡️</div>
                        <h5 className="font-medium text-sm text-cyan-700 dark:text-cyan-200 mb-1">Index Saisonnalité</h5>
                        <p className="text-xl font-bold text-cyan-800 dark:text-cyan-100">
                          {aiAnalysis.metrics.seasonalityIndex ? aiAnalysis.metrics.seasonalityIndex.toFixed(1) : 'N/A'}
                        </p>
                      </div>
                    </Tooltip>
                  )}
                  
                  {aiAnalysis.metrics.growthRate !== undefined && (
                    <Tooltip 
                      content="Le taux de croissance compare les performances actuelles à la période précédente. Positif = croissance, négatif = déclin"
                      position="bottom"
                    >
                      <div className={`text-center p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border ${(aiAnalysis.metrics.growthRate !== undefined && aiAnalysis.metrics.growthRate !== null && aiAnalysis.metrics.growthRate >= 0)
                        ? 'bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/30 dark:to-teal-800/40 border-emerald-300 dark:border-emerald-600' 
                        : 'bg-gradient-to-br from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-800/40 border-rose-300 dark:border-rose-600'}`}>
                        <div className="text-2xl mb-2">{(aiAnalysis.metrics.growthRate !== undefined && aiAnalysis.metrics.growthRate !== null && aiAnalysis.metrics.growthRate >= 0) ? '📈' : '📉'}</div>
                        <h5 className={`font-medium text-sm mb-1 ${(aiAnalysis.metrics.growthRate !== undefined && aiAnalysis.metrics.growthRate !== null && aiAnalysis.metrics.growthRate >= 0)
                          ? 'text-emerald-700 dark:text-emerald-200' 
                          : 'text-rose-700 dark:text-rose-200'}`}>
                          Taux de croissance
                        </h5>
                        <p className={`text-xl font-bold ${(aiAnalysis.metrics.growthRate !== undefined && aiAnalysis.metrics.growthRate !== null && aiAnalysis.metrics.growthRate >= 0)
                          ? 'text-emerald-800 dark:text-emerald-100' 
                          : 'text-rose-800 dark:text-rose-100'}`}>
                          {aiAnalysis.metrics.growthRate !== undefined && aiAnalysis.metrics.growthRate !== null ? 
                            `${aiAnalysis.metrics.growthRate > 0 ? '+' : ''}${aiAnalysis.metrics.growthRate.toFixed(1)}%` : 
                            'N/A'}
                        </p>
                      </div>
                    </Tooltip>
                  )}
                  
                  {aiAnalysis.metrics.peakSeasonRatio !== undefined && (
                    <Tooltip 
                      content="Pourcentage du chiffre d'affaires généré pendant les périodes de pic. Plus c'est élevé, plus l'activité est concentrée sur certaines périodes"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-amber-300 dark:border-amber-600">
                        <div className="text-2xl mb-2">⛰️</div>
                        <h5 className="font-medium text-sm text-amber-700 dark:text-amber-200 mb-1">Pic saisonnier</h5>
                        <p className="text-xl font-bold text-amber-800 dark:text-amber-100">
                          {aiAnalysis.metrics.peakSeasonRatio ? aiAnalysis.metrics.peakSeasonRatio.toFixed(1) : 'N/A'}%
                        </p>
                      </div>
                    </Tooltip>
                  )}
                  
                  {aiAnalysis.metrics.consistencyScore !== undefined && (
                    <Tooltip 
                      content="Score de consistance des ventes sur 100. Plus c'est élevé, plus les ventes sont régulières et prévisibles"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/30 dark:to-purple-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-violet-300 dark:border-violet-600">
                        <div className="text-2xl mb-2">🎯</div>
                        <h5 className="font-medium text-sm text-violet-700 dark:text-violet-200 mb-1">Score Consistance</h5>
                        <p className="text-xl font-bold text-violet-800 dark:text-violet-100">
                          {aiAnalysis.metrics.consistencyScore}/100
                        </p>
                      </div>
                    </Tooltip>
                  )}

                  {aiAnalysis.metrics.peakDayRatio !== undefined && (
                    <Tooltip 
                      content="Pourcentage du chiffre d'affaires généré par le meilleur jour de la période"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-fuchsia-50 to-pink-100 dark:from-fuchsia-900/30 dark:to-pink-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-fuchsia-300 dark:border-fuchsia-600">
                        <div className="text-2xl mb-2">🏆</div>
                        <h5 className="font-medium text-sm text-fuchsia-700 dark:text-fuchsia-200 mb-1">Ratio Pic Quotidien</h5>
                        <p className="text-xl font-bold text-fuchsia-800 dark:text-fuchsia-100">
                          {aiAnalysis.metrics.peakDayRatio ? aiAnalysis.metrics.peakDayRatio.toFixed(1) : 'N/A'}%
                        </p>
                      </div>
                    </Tooltip>
                  )}

                  {aiAnalysis.metrics.weeklyGrowth !== undefined && (
                    <Tooltip 
                      content="Evolution des ventes par rapport à la semaine précédente"
                      position="bottom"
                    >
                      <div className={`text-center p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border ${(aiAnalysis.metrics.weeklyGrowth !== undefined && aiAnalysis.metrics.weeklyGrowth !== null && aiAnalysis.metrics.weeklyGrowth >= 0)
                        ? 'bg-gradient-to-br from-lime-50 to-green-100 dark:from-lime-900/30 dark:to-green-800/40 border-lime-300 dark:border-lime-600' 
                        : 'bg-gradient-to-br from-red-50 to-rose-100 dark:from-red-900/30 dark:to-rose-800/40 border-red-300 dark:border-red-600'}`}>
                        <div className="text-2xl mb-2">{(aiAnalysis.metrics.weeklyGrowth !== undefined && aiAnalysis.metrics.weeklyGrowth !== null && aiAnalysis.metrics.weeklyGrowth >= 0) ? '📊' : '📉'}</div>
                        <h5 className={`font-medium text-sm mb-1 ${(aiAnalysis.metrics.weeklyGrowth !== undefined && aiAnalysis.metrics.weeklyGrowth !== null && aiAnalysis.metrics.weeklyGrowth >= 0)
                          ? 'text-lime-700 dark:text-lime-200' 
                          : 'text-red-700 dark:text-red-200'}`}>
                          Croissance Hebdo
                        </h5>
                        <p className={`text-xl font-bold ${(aiAnalysis.metrics.weeklyGrowth !== undefined && aiAnalysis.metrics.weeklyGrowth !== null && aiAnalysis.metrics.weeklyGrowth >= 0)
                          ? 'text-lime-800 dark:text-lime-100' 
                          : 'text-red-800 dark:text-red-100'}`}>
                          {aiAnalysis.metrics.weeklyGrowth !== undefined && aiAnalysis.metrics.weeklyGrowth !== null ? 
                            `${aiAnalysis.metrics.weeklyGrowth > 0 ? '+' : ''}${aiAnalysis.metrics.weeklyGrowth.toFixed(1)}%` : 
                            'N/A'}
                        </p>
                      </div>
                    </Tooltip>
                  )}

                  {aiAnalysis.metrics.weekendPerformance !== undefined && (
                    <Tooltip 
                      content="Performance des ventes pendant le weekend (samedi et dimanche)"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-100 dark:from-orange-900/30 dark:to-red-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-orange-300 dark:border-orange-600">
                        <div className="text-2xl mb-2">🏖️</div>
                        <h5 className="font-medium text-sm text-orange-700 dark:text-orange-200 mb-1">Perf. Weekend</h5>
                        <p className="text-xl font-bold text-orange-800 dark:text-orange-100">
                          {aiAnalysis.metrics.weekendPerformance ? aiAnalysis.metrics.weekendPerformance.toFixed(1) : 'N/A'}%
                        </p>
                      </div>
                    </Tooltip>
                  )}

                  {aiAnalysis.metrics.monthsWithSales !== undefined && (
                    <Tooltip 
                      content="Nombre de mois avec des ventes enregistrées sur la période analysée"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-blue-300 dark:border-blue-600">
                        <div className="text-2xl mb-2">📅</div>
                        <h5 className="font-medium text-sm text-blue-700 dark:text-blue-200 mb-1">Mois Actifs</h5>
                        <p className="text-xl font-bold text-blue-800 dark:text-blue-100">
                          {aiAnalysis.metrics.monthsWithSales}/12
                        </p>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}

            {/* Insights détaillés de l'IA */}
            {aiAnalysis.insights && Object.keys(aiAnalysis.insights).length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-green-400 mb-4">
                  🧠 Insights d'Analyse Avancée
                </h4>
                <div className="space-y-4">
                  {Object.entries(aiAnalysis.insights).map(([insightType, insightContent]: [string, any]) => {
                    // Vérifier si c'est un JSON string
                    let content = insightContent;
                    if (typeof insightContent === 'string' && insightContent.includes('```json')) {
                      try {
                        // Extraire le JSON du string
                        const jsonMatch = insightContent.match(/```json\n([\s\S]*?)\n```/);
                        if (jsonMatch) {
                          const jsonData = JSON.parse(jsonMatch[1]);
                          content = jsonData;
                        }
                      } catch (e) {
                        // Si parsing échoue, garder le contenu original
                        content = insightContent;
                      }
                    }

                    const getInsightIcon = (type: string) => {
                      const icons: { [key: string]: string } = {
                        seasonalTrends: '🌸',
                        yearlyPerformance: '📈',
                        businessCycles: '🔄',
                        growthAnalysis: '📊',
                        weeklyPatterns: '📅',
                        dailyPerformance: '⏰',
                        customerBehavior: '👥',
                        operationalInsights: '⚙️'
                      };
                      return icons[type] || '💡';
                    };

                    const getInsightTitle = (type: string) => {
                      const titles: { [key: string]: string } = {
                        seasonalTrends: 'Tendances Saisonnières',
                        yearlyPerformance: 'Performance Annuelle',
                        businessCycles: 'Cycles d\'Affaires',
                        growthAnalysis: 'Analyse de Croissance',
                        weeklyPatterns: 'Patterns Hebdomadaires',
                        dailyPerformance: 'Performance Quotidienne',
                        customerBehavior: 'Comportement Client',
                        operationalInsights: 'Insights Opérationnels'
                      };
                      return titles[type] || type;
                    };

                    return (
                      <div key={insightType} className="p-5 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-800 dark:to-gray-800 rounded-xl shadow-sm border border-slate-300 dark:border-slate-600">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-2xl">{getInsightIcon(insightType)}</span>
                          <h5 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
                            {getInsightTitle(insightType)}
                          </h5>
                        </div>
                        
                        {typeof content === 'object' && content.insights ? (
                          // Afficher les insights structurés
                          <div className="space-y-4">
                            {Object.entries(content.insights).map(([key, value]: [string, any]) => (
                              <div key={key} className="pl-4 border-l-3 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-700 rounded-r-lg p-3">
                                <h6 className="font-semibold text-sm text-indigo-700 dark:text-indigo-300 mb-2 capitalize">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </h6>
                                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                                  {typeof value === 'string' ? value : JSON.stringify(value)}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Afficher le contenu simple
                          <div className="pl-4 border-l-3 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-700 rounded-r-lg p-3">
                            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                              {typeof content === 'string' ? content : JSON.stringify(content)}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommandations stratégiques */}
            {aiAnalysis.recommendations && Object.keys(aiAnalysis.recommendations).length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-orange-400 mb-4">
                  💡 Recommandations Stratégiques
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(aiAnalysis.recommendations).map(([category, recommendations]: [string, any]) => (
                    recommendations && recommendations.length > 0 && (
                      <div key={category} className="p-5 bg-gradient-to-br from-white to-slate-50 dark:from-slate-700 dark:to-slate-800 rounded-xl shadow-sm border border-slate-300 dark:border-slate-600">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xl">
                            {category === 'seasonal' ? '🌸' : 
                             category === 'planning' ? '📋' :
                             category === 'marketing' ? '📈' :
                             category === 'inventory' ? '📦' :
                             category === 'staffing' ? '👥' :
                             category === 'operations' ? '⚙️' :
                             '💼'}
                          </span>
                          <h5 className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                            {category === 'seasonal' ? 'Saisonnier' : 
                             category === 'planning' ? 'Planification' :
                             category === 'marketing' ? 'Marketing' :
                             category === 'inventory' ? 'Inventaire' :
                             category === 'staffing' ? 'Personnel' :
                             category === 'operations' ? 'Opérations' :
                             category}
                          </h5>
                        </div>
                        <ul className="space-y-3">
                          {recommendations.slice(0, 3).map((rec: string, index: number) => (
                            <li key={index} className="flex items-start gap-3 p-2 bg-slate-100 dark:bg-slate-600 rounded-lg">
                              <span className="text-indigo-600 dark:text-indigo-400 mt-1 text-sm font-bold">•</span>
                              <span className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Informations sur l'analyse */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Analyse générée automatiquement par IA
                </span>
                <span>
                  {new Date(aiAnalysis.generatedAt).toLocaleDateString('fr-FR')} à {new Date(aiAnalysis.generatedAt).toLocaleTimeString('fr-FR')}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
